import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CalendarGrid, type CellRenderArgs } from '../../components/CalendarGrid';
import { DateRangePicker } from '../../components/DateRangePicker';
import {
  ApiClientError,
  bulkAvailability,
  deleteAvailability,
  fetchAdminCalendar,
  listHutDuties,
  putAvailability,
  type AdminCalendarCell,
  type NewSlotInput,
} from '../../lib/api';
import {
  addMonthsToKey,
  firstOfMonth,
  lastOfMonth,
  monthKey,
} from '../../lib/months';
import { todayInZurich } from '../../lib/today';
import { formatDateLong } from '../../lib/format';
import { CELL_CLASSES, cellTone, occupancyTone } from '../../lib/availability';
import { addDays, useDateRange } from '../../lib/useDateRange';
import { useMonthSwipe } from '../../lib/useMonthSwipe';
import { NewHutDutyDrawer, type ExistingNameRef } from './AdminHutDutyTab';
import { EXTERNAL_GUEST_PASSWORD, GuestLinkModal } from '../../components/GuestLinkModal';

export function AdminCalendarTab() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const today = todayInZurich();
  const [month, setMonth] = useState(monthKey(today));
  const [cells, setCells] = useState<Map<string, AdminCalendarCell>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [existingNames, setExistingNames] = useState<ExistingNameRef[]>([]);
  const [addDutyFor, setAddDutyFor] = useState<string | null>(null);
  const [shareGuestLinkOpen, setShareGuestLinkOpen] = useState(false);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    const ctl = new AbortController();
    listHutDuties()
      .then((rows) => {
        if (ctl.signal.aborted) return;
        setExistingNames(rows.map((r) => ({ id: r.id, name: r.name })));
      })
      .catch(() => {
        // Fail-soft: drawer still works without suggestions.
      });
    return () => ctl.abort();
  }, [refreshKey]);

  useEffect(() => {
    const ctl = new AbortController();
    setError(null);
    fetchAdminCalendar(firstOfMonth(month), lastOfMonth(month), ctl.signal)
      .then((rows) => {
        const m = new Map<string, AdminCalendarCell>();
        for (const r of rows) m.set(r.date, r);
        setCells(m);
      })
      .catch((e: unknown) => {
        if (ctl.signal.aborted) return;
        if (e instanceof ApiClientError) setError(t(`errors.${e.code}`, e.message));
        else setError(t('errors.ERR_INTERNAL'));
      });
    return () => ctl.abort();
  }, [month, refreshKey, t]);

  const monthLabel = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    return new Intl.DateTimeFormat(lang, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(y!, m! - 1, 1)));
  }, [month, lang]);

  const renderCell = ({ date, inMonth }: CellRenderArgs) => {
    if (!inMonth) return null;
    const cell = cells.get(date);
    if (!cell) return null;

    // Closed day: full-cell diagonal stripe (matches public). If a booking
    // still touches a closed day (rare override), surface the rose X/0 badge
    // on top so the admin can spot the conflict.
    if (cell.total_beds === 0) {
      return (
        <>
          <span
            aria-hidden="true"
            className={
              'absolute inset-0 rounded-md border pointer-events-none ' +
              CELL_CLASSES.closed
            }
          />
          {cell.occupied > 0 && (
            <span
              className={
                'relative z-10 block rounded-sm border text-[10px] sm:text-xs ' +
                `px-1 py-0.5 font-medium tabular-nums ${CELL_CLASSES.full}`
              }
            >
              {cell.occupied}/{cell.total_beds}
            </span>
          )}
        </>
      );
    }

    // Names of wardens marked as on duty for this date. Slots can also
    // be stayovers (is_duty=false) — those don't trigger the staffed
    // tint; only real duty slots do.
    const dutyNames = (cell.duties ?? [])
      .filter((d) => d.is_duty)
      .map((d) => d.name);
    const staffed = dutyNames.length > 0;

    // Staffed but no bookings: still show the cell — sky tint + names.
    // Staffed with bookings: tint + names + occupancy badge overlay.
    // Unstaffed + no bookings: nothing.
    if (cell.occupied === 0 && !staffed) return null;

    const tone = cellTone(cell.total_beds, cell.occupied, staffed);

    if (tone === 'staffed') {
      const innerTone = occupancyTone(cell.total_beds, cell.occupied);
      const dutyNamesText = dutyNames.join(', ');
      return (
        <>
          <span
            aria-hidden="true"
            className={
              'absolute inset-0 rounded-md border pointer-events-none ' +
              CELL_CLASSES.staffed
            }
          />
          <div className="relative z-10 flex flex-col gap-0.5 w-full min-w-0">
            <span
              className={
                'block w-full min-w-0 text-[10px] sm:text-xs font-semibold ' +
                'leading-tight text-center break-words text-sky-900 dark:text-sky-200'
              }
              title={dutyNamesText}
            >
              {dutyNamesText}
            </span>
            {cell.occupied > 0 && (
              <span
                className={
                  'block rounded-sm border text-[10px] sm:text-xs px-1 py-0.5 ' +
                  `font-medium tabular-nums ${CELL_CLASSES[innerTone]}`
                }
              >
                {cell.occupied}/{cell.total_beds}
              </span>
            )}
          </div>
        </>
      );
    }

    return (
      <span
        className={
          'block rounded-sm border text-[10px] sm:text-xs px-1 py-0.5 ' +
          `font-medium tabular-nums ${CELL_CLASSES[tone]}`
        }
      >
        {cell.occupied}/{cell.total_beds}
      </span>
    );
  };

  const selected = selectedDate ? cells.get(selectedDate) : null;

  // Admin has no edge guards — both directions are always allowed.
  const swipe = useMonthSwipe({
    onPrev: () => setMonth(addMonthsToKey(month, -1)),
    onNext: () => setMonth(addMonthsToKey(month, 1)),
  });

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h2 className="text-lg font-medium capitalize">{monthLabel}</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShareGuestLinkOpen(true)}
              className="rounded-md border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              {t('admin.calendar.shareGuestLink')}
            </button>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setMonth(addMonthsToKey(month, -1))}
                className="rounded-md border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                aria-label={t('common.previous')}
              >
                ←
              </button>
              <button
                type="button"
                onClick={() => setMonth(addMonthsToKey(month, 1))}
                className="rounded-md border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                aria-label={t('common.next')}
              >
                →
              </button>
            </div>
          </div>
        </div>

        <BulkActions
          month={month}
          onApplied={refresh}
          onError={(code) => setError(t(`errors.${code}`))}
        />

        <div {...swipe} className="touch-pan-y select-none">
          <CalendarGrid month={month} cellRenderer={renderCell} onDateClick={setSelectedDate} />
        </div>

        {error && (
          <p role="alert" className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-3 py-2">
            {error}
          </p>
        )}
      </div>

      <aside className="lg:w-96 shrink-0">
        {selected ? (
          <CalendarSidePanel
            cell={selected}
            lang={lang}
            onClose={() => setSelectedDate(null)}
            onChanged={refresh}
            onAddDuty={() => setAddDutyFor(selected.date)}
          />
        ) : (
          <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-600 p-4 text-sm text-slate-500 dark:text-slate-400">
            {t('admin.calendar.pickADate')}
          </div>
        )}
      </aside>

      {addDutyFor && (
        <NewHutDutyDrawer
          existingNames={existingNames}
          initialSlot={{
            check_in: addDutyFor,
            check_out: addDays(addDutyFor, 1),
            note: null,
            beds_per_night: [0],
            // 1-night slot covers 2 days inclusive (check_in + check_out).
            // Default to "duty on both" — the same as the bulk-paste default.
            is_duty_per_day: [true, true],
          } satisfies NewSlotInput}
          onClose={() => setAddDutyFor(null)}
          onSaved={() => {
            setAddDutyFor(null);
            refresh();
          }}
        />
      )}

      {shareGuestLinkOpen && (
        <GuestLinkModal
          password={EXTERNAL_GUEST_PASSWORD}
          onClose={() => setShareGuestLinkOpen(false)}
        />
      )}
    </div>
  );
}

/* ─── side panel ─────────────────────────────────────────────────────── */

function CalendarSidePanel({
  cell,
  lang,
  onClose,
  onChanged,
  onAddDuty,
}: {
  cell: AdminCalendarCell;
  lang: string;
  onClose(): void;
  onChanged(): void;
  onAddDuty(): void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [beds, setBeds] = useState(String(cell.total_beds));
  const [note, setNote] = useState(cell.note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync local form state when the selected date changes.
  useEffect(() => {
    setBeds(String(cell.total_beds));
    setNote(cell.note ?? '');
    setError(null);
  }, [cell.date, cell.total_beds, cell.note]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const total = Number.parseInt(beds, 10);
      if (!Number.isFinite(total) || total < 0) throw new Error('bad');
      await putAvailability(cell.date, total, note.trim() ? note.trim() : null);
      onChanged();
    } catch (e) {
      if (e instanceof ApiClientError) setError(t(`errors.${e.code}`, e.message));
      else setError(t('errors.ERR_VALIDATION'));
    } finally {
      setBusy(false);
    }
  };

  const revert = async () => {
    setBusy(true);
    try {
      await deleteAvailability(cell.date);
      onChanged();
    } catch (e) {
      if (e instanceof ApiClientError && e.status !== 404) {
        setError(t(`errors.${e.code}`, e.message));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center justify-between p-3 border-b border-slate-200 dark:border-slate-700">
        <h3 className="font-medium">{formatDateLong(cell.date, lang)}</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.cancel')}
          className="rounded-md border border-slate-300 px-2 py-1 text-sm bg-white dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200"
        >
          ×
        </button>
      </div>

      <div className="p-3 space-y-3">
        <div className="text-sm">
          <p>
            {t('admin.calendar.occupiedOfTotal', {
              occupied: cell.occupied,
              total: cell.total_beds,
            })}
          </p>
        </div>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">{t('admin.calendar.editCapacity')}</legend>
          <label className="block text-sm">
            <span className="block mb-1 text-slate-600 dark:text-slate-400">{t('admin.calendar.totalBeds')}</span>
            <input
              type="number"
              min={0}
              value={beds}
              onChange={(e) => setBeds(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
            />
          </label>
          <label className="block text-sm">
            <span className="block mb-1 text-slate-600 dark:text-slate-400">
              {t('admin.calendar.internalNote')}
            </span>
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
            />
          </label>
          {error && (
            <p role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-2 py-1">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-3 py-1.5 text-sm font-medium disabled:opacity-60"
            >
              {t('common.save')}
            </button>
            <button
              type="button"
              onClick={revert}
              disabled={busy}
              className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-1.5 text-sm"
            >
              {t('admin.calendar.revertDefault')}
            </button>
          </div>
        </fieldset>

        <div>
          <h4 className="text-sm font-medium mb-2">
            {t('admin.calendar.stays', { count: cell.stays.length })}
          </h4>
          {cell.stays.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">{t('admin.calendar.noStays')}</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {cell.stays.map((s) => (
                <li
                  key={s.stay_id}
                  className="border border-slate-200 rounded-md px-2 py-1.5 flex justify-between items-center dark:border-slate-700"
                >
                  <span className="font-medium truncate">{s.name}</span>
                  <span className="tabular-nums text-xs text-slate-600 dark:text-slate-400">
                    {s.members_adult + s.members_child + s.guests_adult + s.guests_child}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <h4 className="text-sm font-medium mb-2">{t('admin.calendar.dutiesTitle')}</h4>
          {cell.duties && cell.duties.length > 0 && (
            <ul className="space-y-1 text-sm mb-2">
              {cell.duties.map((d) => (
                <li key={d.slot_id}>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/admin/dashboard/hutteneinsatz?slot=${d.slot_id}` +
                          `&name=${encodeURIComponent(d.name)}`,
                      )
                    }
                    className={
                      'w-full text-left border rounded-md px-2 py-1.5 flex justify-between items-center gap-2 ' +
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 ' +
                      (d.is_duty
                        ? 'border-sky-300 bg-sky-50 hover:bg-sky-100 dark:border-sky-700 dark:bg-sky-950 dark:hover:bg-sky-900/60'
                        : 'border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800')
                    }
                  >
                    <span className="font-medium truncate">{d.name}</span>
                    <span className="flex items-center gap-2">
                      {d.beds > 0 && (
                        <span className="tabular-nums text-xs text-slate-600 dark:text-slate-400">
                          {d.beds} {t('admin.hutDuty.bedsShort')}
                        </span>
                      )}
                      <span
                        className={
                          'inline-flex items-center rounded-full text-xs font-medium px-2 py-0.5 ' +
                          (d.is_duty
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200'
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300')
                        }
                      >
                        {d.is_duty
                          ? t('admin.hutDuty.dutyLabel')
                          : t('admin.hutDuty.stayoverLabel')}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            onClick={onAddDuty}
            className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-1.5 text-sm"
          >
            + {t('admin.calendar.addHutDuty')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── bulk actions ───────────────────────────────────────────────────── */

function BulkActions({
  month,
  onApplied,
  onError,
}: {
  month: string;
  onApplied(): void;
  onError(code: string): void;
}) {
  const { t } = useTranslation();
  const { from, to, reset } = useDateRange({
    initialFrom: firstOfMonth(month),
    initialTo: lastOfMonth(month),
  });
  const [beds, setBeds] = useState('26');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  // Sync default range to the visible month — bypass the coupling, both
  // ends are set in one shot.
  useEffect(() => {
    reset(firstOfMonth(month), lastOfMonth(month));
  }, [month, reset]);

  const submit = async (totalBeds: number, defaultNote?: string) => {
    setBusy(true);
    try {
      await bulkAvailability(from, to, totalBeds, (defaultNote ?? note).trim() || null);
      onApplied();
    } catch (e) {
      if (e instanceof ApiClientError) onError(e.code);
      else onError('ERR_INTERNAL');
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="mb-3 rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900 p-3">
      <summary className="cursor-pointer text-sm font-medium select-none">
        {t('admin.calendar.bulkActions')}
      </summary>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
        <label className="text-sm col-span-2 min-w-0">
          <span className="block mb-1 text-slate-600">
            {t('admin.calendar.from')} – {t('admin.calendar.to')}
          </span>
          <DateRangePicker from={from} to={to} onChange={reset} />
        </label>
        <label className="text-sm">
          <span className="block mb-1 text-slate-600">{t('admin.calendar.totalBeds')}</span>
          <input
            type="number"
            min={0}
            value={beds}
            onChange={(e) => setBeds(e.target.value)}
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="block mb-1 text-slate-600">{t('admin.calendar.internalNote')}</span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-2 py-1.5 text-sm"
          />
        </label>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            const n = Number.parseInt(beds, 10);
            if (Number.isFinite(n) && n >= 0) void submit(n);
          }}
          className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-3 py-1.5 text-sm font-medium disabled:opacity-60"
        >
          {t('admin.calendar.setRange')}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void submit(0)}
          className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-1.5 text-sm"
        >
          {t('admin.calendar.blockRange')}
        </button>
      </div>
    </details>
  );
}
